import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { uploadPdf } from './uploadPdf.api'
import { UploadPdfResponse200, UploadPdfResponse202, UploadPdfState, UploadPdfSuccessResponse } from './uploadPdf.types'

const initialState: UploadPdfState = {
  loading: false,
  error: null,
  uploadProgress: 0,
  isProcessing: false,
  extractedData: null,
  extractionStatus: null,
  extractionAttemptId: null,
  confidence: null,
  requestId: null,
  estimatedReviewTime: null,
  pdfUrl: null,
  pdfExpiresAt: null,
  warnings: []
}

const slice = createSlice({
  name: 'externalFlightBookings/uploadPdf',
  initialState,
  reducers: {
    setUploadProgress(state, action: PayloadAction<number>) {
      state.uploadProgress = action.payload
      // Once upload reaches 95%+, switch to processing state
      // Note: Axios onUploadProgress doesn't always fire at exactly 100%
      if (action.payload >= 95 && !state.isProcessing) {
        state.isProcessing = true
      }
    },
    resetUploadState() {
      return initialState
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadPdf.pending, (state) => {
        state.loading = true
        state.error = null
        state.uploadProgress = 0
        state.isProcessing = false
      })
      .addCase(uploadPdf.fulfilled, (state, action) => {
        state.loading = false
        state.isProcessing = false
        state.uploadProgress = 100
        const data = action.payload as UploadPdfSuccessResponse
        state.extractionStatus = data.extractionStatus
        state.extractionAttemptId = data.extractionAttemptId ?? null
        state.confidence = data.extraction_meta?.confidence ?? data.confidence ?? null
        state.pdfUrl = data.pdfUrl ?? null
        state.pdfExpiresAt = data.pdfExpiresAt ?? null
        state.warnings = data.warnings ?? []

        if (data.extractionStatus === 'ready_for_confirmation') {
          const response200 = data as UploadPdfResponse200
          state.extractedData = response200.flightItinerary || null
        } else {
          const response202 = data as UploadPdfResponse202
          state.requestId = response202.requestId ?? null
          state.estimatedReviewTime = response202.estimatedReviewTime ?? null
        }
      })
      .addCase(uploadPdf.rejected, (state, action) => {
        state.loading = false
        state.isProcessing = false
        state.error = (action.payload as string) || 'Upload failed'
      })
  }
})

export const { setUploadProgress, resetUploadState } = slice.actions
export default slice.reducer
